// API Client for FluxFeed Backend

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // Use environment variable if set
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }

  // In production (Cloudflare Pages), use the full backend URL
  if (import.meta.env.PROD || window.location.hostname.includes('pages.dev')) {
    return 'https://fluxfeed-api.kozan26.workers.dev/api';
  }

  // In development, use relative path (proxied by Vite)
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Types matching backend
export interface User {
  id: number;
  email: string;
  name: string | null;
  preferences: Record<string, any>;
  created_at: number;
  updated_at: number;
}

export interface Folder {
  id: number;
  user_id: number;
  name: string;
  parent_folder_id: number | null;
  position: number;
  color: string;
  created_at: number;
}

export interface Feed {
  id: number;
  user_id: number;
  folder_id: number | null;
  url: string;
  title: string;
  site_url: string | null;
  favicon_url: string | null;
  description: string | null;
  fetch_interval: number;
  last_fetched_at: number | null;
  etag: string | null;
  status: 'active' | 'paused' | 'error';
  error_count: number;
  last_error: string | null;
  created_at: number;
  updated_at: number;
}

export interface Article {
  id: number;
  feed_id: number;
  guid: string;
  title: string;
  url: string;
  author: string | null;
  content_html: string | null;
  content_markdown: string | null;
  summary_ai: string | null;
  priority_score: number;
  published_at: number;
  read: number;
  starred: number;
  archived: number;
  created_at: number;
}

export interface Tag {
  id: number;
  user_id: number;
  name: string;
  color: string;
  created_at: number;
}

export interface CountersResponse {
  counters: {
    feeds: { feed_id: number; total: number; unread: number }[];
    folders: { folder_id: number | null; total: number; unread: number }[];
    tags: { tag_id: number; total: number; unread: number }[];
  };
}

class ApiClient {
  private async request<T>(
    path: string,
    options?: RequestInit
  ): Promise<T> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // User endpoints
  async getUser() {
    return this.request<{ user: User }>('/user/me');
  }

  async updatePreferences(preferences: Record<string, any>) {
    return this.request<{ preferences: Record<string, any> }>('/user/preferences', {
      method: 'PATCH',
      body: JSON.stringify({ preferences }),
    });
  }

  async getUserStats() {
    return this.request<{ stats: any }>('/user/stats');
  }

  async getCounters() {
    return this.request<CountersResponse>('/user/counters');
  }

  // Feed endpoints
  async getFeeds() {
    return this.request<{ feeds: Feed[] }>('/feeds');
  }

  async getFeed(id: number) {
    return this.request<{ feed: Feed }>(`/feeds/${id}`);
  }

  async createFeed(data: { url: string; folder_id?: number | null }) {
    return this.request<{ feed: Feed }>('/feeds', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFeed(id: number, data: Partial<Feed>) {
    return this.request<{ feed: Feed }>(`/feeds/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFeed(id: number) {
    return this.request<{ success: boolean }>(`/feeds/${id}`, {
      method: 'DELETE',
    });
  }

  async refreshFeed(id: number) {
    return this.request<{ success: boolean; message: string }>(`/feeds/${id}/refresh`, {
      method: 'POST',
    });
  }

  // Folder endpoints
  async getFolders() {
    return this.request<{ folders: Folder[] }>('/folders');
  }

  async createFolder(data: { name: string; parent_folder_id?: number | null; color?: string }) {
    return this.request<{ folder: Folder }>('/folders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFolder(id: number, data: Partial<Folder>) {
    return this.request<{ folder: Folder }>(`/folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFolder(id: number) {
    return this.request<{ success: boolean }>(`/folders/${id}`, {
      method: 'DELETE',
    });
  }

  // Article endpoints
  async getArticles(params?: {
    feed_id?: number;
    folder_id?: number;
    tag_id?: number;
    unread?: boolean;
    starred?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query.append(key, String(value));
        }
      });
    }
    const url = `/articles${query.toString() ? `?${query}` : ''}`;
    console.log('API getArticles URL:', url);
    return this.request<{ articles: Article[] }>(url);
  }

  async getArticle(id: number) {
    return this.request<{ article: Article; tags: Tag[] }>(`/articles/${id}`);
  }

  async updateArticle(id: number, data: { read?: boolean; starred?: boolean; archived?: boolean }) {
    return this.request<{ article: Article }>(`/articles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdateArticles(filters: {
    ids?: number[];
    feed_id?: number;
    folder_id?: number;
    tag_id?: number;
    filter_unread?: boolean;
    filter_starred?: boolean;
    read?: boolean;
    starred?: boolean;
    archived?: boolean;
  }) {
    return this.request<{ success: boolean }>(`/articles/bulk`, {
      method: 'PATCH',
      body: JSON.stringify(filters),
    });
  }

  async summarizeArticle(id: number) {
    return this.request<{ summary: string }>(`/articles/${id}/summarize`, {
      method: 'POST',
    });
  }

  async suggestTags(id: number) {
    return this.request<{ suggested_tags: string[] }>(`/articles/${id}/suggest-tags`, {
      method: 'POST',
    });
  }

  async fetchArticleContent(id: number) {
    return this.request<{ content: { markdown: string; html: string; title?: string } }>(`/articles/${id}/fetch-content`, {
      method: 'POST',
    });
  }

  // Tag endpoints
  async getTags() {
    return this.request<{ tags: Tag[] }>('/tags');
  }

  async createTag(data: { name: string; color?: string }) {
    return this.request<{ tag: Tag }>('/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTag(id: number, data: Partial<Tag>) {
    return this.request<{ tag: Tag }>(`/tags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteTag(id: number) {
    return this.request<{ success: boolean }>(`/tags/${id}`, {
      method: 'DELETE',
    });
  }

  async tagArticle(tagId: number, articleId: number) {
    return this.request<{ success: boolean }>(`/tags/${tagId}/articles/${articleId}`, {
      method: 'POST',
    });
  }

  async untagArticle(tagId: number, articleId: number) {
    return this.request<{ success: boolean }>(`/tags/${tagId}/articles/${articleId}`, {
      method: 'DELETE',
    });
  }

  // Reordering endpoints
  async reorderFeeds(itemIds: number[]) {
    return this.request<{ success: boolean }>('/feeds/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ itemIds }),
    });
  }

  async reorderFolders(itemIds: number[]) {
    return this.request<{ success: boolean }>('/folders/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ itemIds }),
    });
  }

  async reorderTags(itemIds: number[]) {
    return this.request<{ success: boolean }>('/tags/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ itemIds }),
    });
  }
}

export const api = new ApiClient();
